import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** Native fullscreen where available; the same CSS view works on iPhone without that API. */
export function useGardenFullscreen(target: RefObject<HTMLElement | null>, practicing: boolean) {
  const [expanded, setExpanded] = useState(false);
  const wasNative = useRef(false), requested = useRef(false), trigger = useRef<HTMLElement | null>(null);
  const exit = useCallback(async () => {
    requested.current = false;
    setExpanded(false);
    if (document.fullscreenElement === target.current) await document.exitFullscreen().catch(() => {});
    requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true }));
  }, [target]);
  const enter = async (source?: HTMLElement) => {
    trigger.current = source ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    requested.current = true;
    setExpanded(true);
    if (target.current?.requestFullscreen && document.fullscreenEnabled) {
      try { await target.current.requestFullscreen(); wasNative.current = true; if (!requested.current) await document.exitFullscreen(); }
      catch { /* The CSS view remains available when the browser declines native fullscreen. */ }
    }
  };
  useEffect(() => {
    document.documentElement.classList.toggle('garden-expanded', expanded);
    return () => document.documentElement.classList.remove('garden-expanded');
  }, [expanded]);
  useEffect(() => {
    const changed = () => {
      if (document.fullscreenElement === target.current) wasNative.current = true;
      else if (wasNative.current) { wasNative.current = false; requested.current = false; setExpanded(false); trigger.current?.focus({ preventScroll: true }); }
    };
    document.addEventListener('fullscreenchange', changed);
    return () => document.removeEventListener('fullscreenchange', changed);
  }, [target]);
  useEffect(() => {
    if (!expanded || practicing) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('dialog[open]')) { event.preventDefault(); void exit(); }
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [expanded, practicing, exit]);
  return { expanded, enter, exit };
}
